import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Alert,
  Box,
  Avatar,
  CircularProgress,
} from "@mui/material";
import {
  Block as BlockIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import { red, green } from "@mui/material/colors";

const UserBanModal = ({
  open,
  user,
  action, // 'ban' or 'unban'
  onClose,
  onConfirm,
  loading,
}) => {
  const [reason, setReason] = useState("");
  const [formError, setFormError] = useState("");

  useEffect(() => {
    if (open) {
      setReason("");
      setFormError("");
    }
  }, [open]);

  const handleReasonChange = (e) => {
    setReason(e.target.value);
    if (formError) {
      setFormError("");
    }
  };

  const handleConfirm = () => {
    // Validate reason for ban action
    if (action === "ban" && !reason.trim()) {
      setFormError("Please provide a reason for banning this user");
      return;
    }

    if (action === "ban" && reason.trim().length < 5) {
      setFormError("Reason must be at least 5 characters long");
      return;
    }

    onConfirm(reason.trim());
  };

  const getActionConfig = () => {
    if (action === "ban") {
      return {
        title: "Ban User",
        icon: <BlockIcon sx={{ color: red[500] }} />,
        color: red[500],
        actionText: "ban",
        confirmButtonColor: "error",
        confirmButtonText: "Ban User",
      };
    } else {
      return {
        title: "Unban User",
        icon: <CheckCircleOutlineIcon sx={{ color: green[500] }} />,
        color: green[500],
        actionText: "unban",
        confirmButtonColor: "success",
        confirmButtonText: "Unban User",
      };
    }
  };

  if (!user || !action) return null;

  const config = getActionConfig();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {config.icon}
          <Typography variant="h6">{config.title}</Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* User Info */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            p: 2,
            backgroundColor: "grey.50",
            borderRadius: 1,
            mb: 3,
          }}
        >
          <Avatar
            src={user.profile_image_url}
            sx={{
              bgcolor: config.color,
              width: 50,
              height: 50,
            }}
          >
            {user.username?.charAt(0)?.toUpperCase() ||
              user.email?.charAt(0)?.toUpperCase() ||
              "U"}
          </Avatar>
          <Box>
            <Typography variant="subtitle1" fontWeight="medium">
              {user.username || "No username"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {user.email}
            </Typography>
          </Box>
        </Box>

        {/* Warning/Info Alert */}
        <Alert
          severity={action === "ban" ? "warning" : "info"}
          icon={action === "ban" ? <WarningIcon /> : <CheckCircleOutlineIcon />}
          sx={{ mb: 3 }}
        >
          <Typography variant="body2">
            {action === "ban" ? (
              <>
                You are about to <strong>ban</strong> this user. They will be
                prevented from accessing the platform and their account will be
                marked as banned.
              </>
            ) : (
              <>
                You are about to <strong>unban</strong> this user. They will
                regain access to the platform and their account will be restored
                to normal status.
              </>
            )}
          </Typography>
        </Alert>

        {/* Current Ban Info for Unban */}
        {action === "unban" && user.ban_info && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Current Ban Information:
            </Typography>
            <Box sx={{ pl: 2 }}>
              {user.ban_info.reason && (
                <Typography variant="body2" color="text.secondary">
                  <strong>Reason:</strong> {user.ban_info.reason}
                </Typography>
              )}
              {user.ban_info.created_at && (
                <Typography variant="body2" color="text.secondary">
                  <strong>Banned on:</strong>{" "}
                  {new Date(user.ban_info.created_at).toLocaleString()}
                </Typography>
              )}
            </Box>
          </Box>
        )}

        {/* Reason Input for Ban */}
        {action === "ban" && (
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Reason for ban *"
            placeholder="Please provide a detailed reason for banning this user..."
            value={reason}
            onChange={handleReasonChange}
            error={!!formError}
            helperText={formError}
            variant="outlined"
          />
        )}

        {/* Optional Reason for Unban */}
        {action === "unban" && (
          <TextField
            fullWidth
            multiline
            rows={2}
            label="Additional notes (optional)"
            placeholder="Any additional notes about this unban action..."
            value={reason}
            onChange={handleReasonChange}
            variant="outlined"
          />
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="outlined" color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color={config.confirmButtonColor}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} /> : config.icon}
        >
          {loading ? "Processing..." : config.confirmButtonText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserBanModal;
